import React, { useEffect, useState, useRef } from 'react'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  AppBar,
  Toolbar,
  TextField,
  IconButton,
  ThemeProvider,
  StyledEngineProvider,
  Tooltip,
  Paper,
} from '@mui/material'
import Close from '@mui/icons-material/Close'
import FilterList from '@mui/icons-material/FilterList'
import Autocomplete from '@mui/material/Autocomplete'
import Draggable from 'react-draggable'
import orderBy from 'lodash/orderBy'
import Pagination from '../../Pagination/Pagination'
import Typography from '../../Typography/Typography'
import Button from '../../Button/Button'
import {
  texts,
  getColumnDefs,
  getClaimReleaseGridOptions,
} from './claim-release-utils'
import AppBarInputFieldTheme from './AppBarInputFieldTheme'
import {
  getFilterRequest,
  getSortRequest,
} from '../../../utils/sortFilterService'
import { DataGrid } from 'apex-ui-kit-pcn'

const PREFIX = 'ClaimReleaseItems'

const classes = {
  appBar: `${PREFIX}-appBar`,
  bottomBar: `${PREFIX}-bottomBar`,
  flex: `${PREFIX}-flex`,
  claimReleaseButton: `${PREFIX}-claimReleaseButton`,
  actionBarColor: `${PREFIX}-actionBarColor`,
  clearFilterIcon: `${PREFIX}-clearFilterIcon`,
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.appBar}`]: {
    backgroundColor: '#8F1C20',
    color: '#fff',
    position: 'static',
  },

  [`& .${classes.bottomBar}`]: {
    top: 'auto',
    bottom: 0,
    backgroundColor: '#366CD9',
    color: '#fff',
    position: 'static',
  },

  [`& .${classes.flex}`]: {
    flexGrow: 1,
  },

  [`& .${classes.claimReleaseButton}`]: {
    color: '#fff',
  },

  [`& .${classes.actionBarColor}`]: {
    color: '#fff',
  },

  [`& .${classes.clearFilterIcon}`]: {
    fontSize: '15px',
    marginTop: '10px',
    marginLeft: '-10px',
  },
}))

const PaperComponent = props => {
  return (
    <Draggable cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  )
}

const ClaimReleaseItems = props => {
  const {
    getItems,
    claimItems,
    releaseItems,
    handleDialogClose,
    action = 'claim',
    context,
    getEventsOrPlans,
  } = props
  const [showDialog, setShowDialog] = useState(!!props.showDialog)
  const [selectedEventOrPlan, setSelectedEventOrPlan] = useState()
  const [page, setPage] = useState(1)
  const [perPage, setRowsPerPage] = useState(1000)
  const [itemCount, setItemCount] = useState(0)
  const [isFilterActive, setIsFilterActive] = useState(false)
  const eventsOrPlans = useRef([])
  const [selectedItems, setSelectedItems] = useState([])
  const columnDefs = getColumnDefs(context, action)
  const contextWordings = texts[context][action]

  useEffect(() => {
    getRowData()
  }, [page, perPage]) // eslint-disable-line

  useEffect(() => {
    setShowDialog(props.showDialog)
  }, [props.showDialog])

  const onRowSelectionChanged = async params => {
    if (!eventsOrPlans.current.length) {
      const authorizedEventsOrPlans = await getEventsOrPlans()
      eventsOrPlans.current = orderBy(
        authorizedEventsOrPlans,
        ['groupBy', contextWordings.sortBy],
        ['desc', 'asc'],
      )
    }
    const selectedNodes = params.api.getSelectedNodes()
    setSelectedItems(selectedNodes.map(node => node.data))
  }

  const getRowData = async params => {
    try {
      gridOptions.autoGroupColumnDef = {
        field: 'section_data.item.description',
      }

      if (params) {
        const sortFilterRequest = {
          filter_request: getFilterRequest(params),
          sort_request: getSortRequest(params, gridOptions),
        }
        const gridData = await getItems(sortFilterRequest, page, perPage)
        setItemCount(gridData.count)
        setIsFilterActive(params.isAnyFilterPresent())
        await params.applyTransaction({ add: gridData.values })
      }
    } catch (error) {
      console.error('Error fetching or rendering data:', error)
    }
  }

  let isInitialLoad = false
  const onGridReadyFunction = async params => {
    let gridApi = params.api
    if (!isInitialLoad) {
      await getRowData(gridApi)
      isInitialLoad = true
    }
  }

  const gridOptionsRef = useRef({
    ...getClaimReleaseGridOptions(),
    onSelectionChanged: onRowSelectionChanged,
    onFilterChanged: getRowData,
    onSortChanged: getRowData,
    onGridReady: onGridReadyFunction,
  })

  const gridOptions = gridOptionsRef.current

  const resetPage = () => {
    const isFilterPresent = gridOptions.api.isAnyFilterPresent()
    const isSortPresent = gridOptions.api.getColumnState().length > 0
    if (!isFilterPresent && !isSortPresent) {
      getRowData()
    } else {
      if (isFilterPresent) {
        gridOptions.api.setFilterModel(null)
      }
      if (isSortPresent) {
        gridOptions.api.applyColumnState({ state: null })
      }
    }
    setSelectedItems([])
    setPage(1)
    setRowsPerPage(1000)
  }

  const submit = async action => {
    const selectedRows = gridOptions.api.getSelectedRows()
    const updateArray = selectedRows.reduce((accumulator, currentValue) => {
      if (context === 'plan') {
        accumulator.push({
          id: currentValue.id,
          event_id: action === 'claim' ? selectedEventOrPlan.id : null,
        })
      } else {
        accumulator.push({
          id: currentValue.id,
          plan_id: action === 'claim' ? selectedEventOrPlan.plan_id : null,
        })
      }
      return accumulator
    }, [])
    const actionCallback = action === 'claim' ? claimItems : releaseItems
    await actionCallback(updateArray)
    resetPage()
  }

  const handleEventInputChange = (action, value) => {
    if (action === 'clear') {
      setSelectedEventOrPlan(null)
    }
  }

  const getBottomBar = () => {
    return (
      selectedItems.length > 0 && (
        <AppBar id="bottom-app-bar" className={classes.bottomBar}>
          <Toolbar>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={2}
            >
              <Grid item>
                <Typography variant="h3" color="inherit">
                  {selectedItems.length} Selected
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Autocomplete
                  id="events-or-plans-list"
                  options={eventsOrPlans.current}
                  autoComplete={true}
                  size="small"
                  groupBy={option => option.groupBy}
                  onInputChange={(e, value, reason) =>
                    handleEventInputChange(reason, value)
                  }
                  onChange={(e, value) => setSelectedEventOrPlan(value)}
                  classes={{
                    clearIndicator: classes.actionBarColor,
                    inputRoot: classes.actionBarColor,
                    popupIndicator: classes.actionBarColor,
                  }}
                  getOptionLabel={option =>
                    `${option[contextWordings.optionLabelKeys[0]]} - ${
                      option[contextWordings.optionLabelKeys[1]]
                    }`
                  }
                  renderInput={params => (
                    <StyledEngineProvider injectFirst>
                      <ThemeProvider theme={AppBarInputFieldTheme}>
                        <TextField
                          {...params}
                          label={contextWordings.dropDownLabel}
                          variant="outlined"
                          fullWidth
                        />
                      </ThemeProvider>
                    </StyledEngineProvider>
                  )}
                />
              </Grid>
              <Grid item>
                <Tooltip title={contextWordings.claimButtonTooltip}>
                  <Box component={'span'}>
                    <Button
                      disabled={!selectedEventOrPlan}
                      id="claim-btn"
                      size="medium"
                      color="tertiary"
                      onClick={() => submit('claim')}
                    >
                      {contextWordings.claimButton}
                    </Button>
                  </Box>
                </Tooltip>
              </Grid>
              {action === 'release' && (
                <Grid item>
                  <Tooltip title={contextWordings.releaseButtonTooltip}>
                    <Box component={'span'}>
                      <Button
                        id="release-btn"
                        size="medium"
                        color="tertiary"
                        onClick={() => submit('release')}
                        classes={{
                          root: classes.claimReleaseButton,
                        }}
                      >
                        {contextWordings.releaseButton}
                      </Button>
                    </Box>
                  </Tooltip>
                </Grid>
              )}
            </Grid>
          </Toolbar>
        </AppBar>
      )
    )
  }

  return (
    <StyledDialog
      maxWidth={'xl'}
      open={showDialog}
      fullWidth={true}
      PaperComponent={PaperComponent}
      id="claim-release-dialog"
    >
      <AppBar classes={{ root: classes.appBar }}>
        <Toolbar>
          <Typography variant="h3" color="inherit" className={classes.flex}>
            {contextWordings.header}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            aria-label="close"
            onClick={handleDialogClose}
            id="claim-release-dialog-close"
            size="large"
          >
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent>
        <Grid container spacing={3} direction="row">
          <Grid item xs={9}>
            {context === 'plan' && action === 'claim' && (
              <strong>{itemCount} unclaimed products.</strong>
            )}
            {contextWordings.info}
          </Grid>
          <Grid container item xs={3} justifyContent="flex-end">
            <Tooltip title="Clear all filters">
              <Box component={'span'}>
                <IconButton
                  color="inherit"
                  aria-label="clear all filters"
                  size="small"
                  onClick={() => gridOptions.api.setFilterModel(null)}
                  disabled={!isFilterActive}
                  id="ClearFilter"
                >
                  <FilterList />
                  <Close classes={{ root: classes.clearFilterIcon }} />{' '}
                </IconButton>
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
        <Box mt={1} component={'div'}>
          <DataGrid
            rowData={gridOptions.rowData}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            height="calc(100vh - 350px)"
            width="100%"
          />
          <Pagination
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 256,
                    minHeight: 48,
                  },
                },
              },
            }}
            colSpan={3}
            component="div"
            id="claim-release-pagination"
            count={itemCount}
            page={page}
            rowsPerPage={perPage}
            rowsPerPageOptions={[100, 500, 1000]}
            onChangePage={(event, newPage) => setPage(newPage)}
            onChangeRowsPerPage={event =>
              setRowsPerPage(parseInt(event.target.value, 10))
            }
          />
        </Box>
      </DialogContent>
      {getBottomBar()}
    </StyledDialog>
  )
}

ClaimReleaseItems.propTypes = {
  showDialog: PropTypes.bool.isRequired,
  action: PropTypes.oneOf(['claim', 'release']),
  context: PropTypes.oneOf(['plan', 'event']),
  getItems: PropTypes.func.isRequired,
  claimItems: PropTypes.func.isRequired,
  releaseItems: PropTypes.func,
  getEventsOrPlans: PropTypes.func.isRequired,
  handleDialogClose: PropTypes.func.isRequired,
}

export default ClaimReleaseItems
